<template>
  <b-modal
    id="quick-create-car-modal"
    ref="quickCreateCarModal"
    title="Création rapide d'un véhicule"
    size="md"
    hide-footer
    centered
    modal-class="modal-primary"
    @hidden="resetForm"
  >
    <validation-observer ref="simpleRules">
      <b-form @submit.prevent="submitCar">
        <b-row>
          <!-- Matricule (serial_number) -->
          <b-col cols="12">
            <b-form-group
              label="Matricule *"
              label-for="quick-car-serial"
              description="Ex: 123 TUN 4567, 9876-TU-200"
            >
              <validation-provider
                #default="{ errors }"
                name="Matricule"
                rules="required"
              >
                <b-form-input
                  id="quick-car-serial"
                  v-model="car.serial_number"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Entrez le numéro de matricule"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Marque (brand) -->
          <b-col md="6">
            <b-form-group
              label="Marque *"
              label-for="quick-car-brand"
            >
              <validation-provider
                #default="{ errors }"
                name="Marque"
                rules="required"
              >
                <b-form-input
                  id="quick-car-brand"
                  v-model="car.brand"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Ex: Peugeot, Renault"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Couleur (color) -->
          <b-col md="6">
            <b-form-group
              label="Couleur *"
              label-for="quick-car-color"
            >
              <validation-provider
                #default="{ errors }"
                name="Couleur"
                rules="required"
              >
                <v-select
                  id="quick-car-color"
                  v-model="car.color"
                  :options="colorOptions"
                  placeholder="Choisir ou chercher une couleur"
                  taggable
                  push-tags
                  class="select-search-option"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Modèle (model - optionnel) -->
          <b-col cols="12">
            <b-form-group
              label="Modèle (Optionnel)"
              label-for="quick-car-model"
            >
              <b-form-input
                id="quick-car-model"
                v-model="car.model"
                placeholder="Ex: Partner, Berlingo, Clio"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            :disabled="isLoading"
            @click="hide"
          >
            Annuler
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="isLoading"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-1"
            />
            <span>{{ isLoading ? 'Création...' : 'Créer et appliquer' }}</span>
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BRow,
  BCol,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'QuickCreateCarModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  data() {
    return {
      car: {
        serial_number: '',
        brand: '',
        color: '',
        model: '',
      },
      colorOptions: [
        'Blanc',
        'Noir',
        'Gris',
        'Argent',
        'Rouge',
        'Bleu',
        'Beige',
        'Marron',
        'Vert',
        'Jaune',
        'Orange',
        'Bordeaux',
        'Violet',
      ],
      isLoading: false,
    }
  },
  methods: {
    show() {
      this.$refs.quickCreateCarModal.show()
    },
    hide() {
      this.$refs.quickCreateCarModal.hide()
    },
    resetForm() {
      this.car = {
        serial_number: '',
        brand: '',
        color: '',
        model: '',
      }
      this.isLoading = false
    },
    async submitCar() {
      const isValid = await this.$refs.simpleRules.validate()
      if (!isValid) return

      this.isLoading = true
      try {
        let rawColor = ''
        if (typeof this.car.color === 'string') {
          rawColor = this.car.color
        } else if (this.car.color) {
          rawColor = String(this.car.color)
        }
        const payload = {
          serial_number: this.car.serial_number.trim(),
          brand: this.car.brand.trim(),
          color: rawColor.trim() || 'Inconnue',
          model: this.car.model ? this.car.model.trim() : null,
          status: 'available',
          is_active: true,
        }

        const { data } = await axios.post('/api/cars/create/', payload)

        this.showToast('success', 'Véhicule créé', `Le véhicule "${data.serial_number}" a été créé avec succès.`)
        this.$emit('car-created', data)
        this.hide()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating car:', error)
        let errorMsg = 'Erreur lors de la création du véhicule'
        if (error.response && error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMsg = error.response.data
          } else if (error.response.data.detail) {
            errorMsg = error.response.data.detail
          } else if (typeof error.response.data === 'object') {
            const msgs = Object.values(error.response.data).flat()
            if (msgs.length > 0) errorMsg = msgs.join(' ')
          }
        }
        this.showToast('danger', 'Erreur', errorMsg)
      } finally {
        this.isLoading = false
      }
    },
    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          variant,
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
        },
      })
    },
  },
}
</script>
